import { FC, useEffect, useState } from 'react';

import { RadioButtonGroup } from '@purpur/library';
import _ from 'lodash';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { RangeSlider } from '@telialt/common-range-slider/src';
import { useComponentId } from '@telialt/utils/src/hooks';

import { SEARCH_TEST_ID } from '../../test/test-ids';
import { PriceTypeRadioContainer, PriceTypeTitleContainer } from './FacetPriceFilter.styles';

type PriceFilterType = 'MONTHLY' | 'FULL';

type Props = {
    minMonthlyPrice?: number;
    maxMonthlyPrice?: number;
    minFullPrice?: number;
    maxFullPrice?: number;
    defaultSelectedType?: PriceFilterType;
    defaultFromValue?: number;
    defaultToValue?: number;
    onChange: (type: PriceFilterType, from: number, to: number) => void;
};

export const FacetPriceFilter: FC<Props> = ({
    minMonthlyPrice,
    maxMonthlyPrice,
    minFullPrice,
    maxFullPrice,
    defaultSelectedType = 'MONTHLY',
    defaultFromValue,
    defaultToValue,
    onChange,
}) => {
    const { t } = useTranslations();

    const componentId = useComponentId();

    const [selectedType, setSelectedType] = useState(defaultSelectedType);
    const [selectedFromValue, setSelectedFromValue] = useState(defaultFromValue);
    const [selectedToValue, setSelectedToValue] = useState(defaultToValue);

    useEffect(() => {
        setSelectedType(defaultSelectedType);
    }, [defaultSelectedType]);

    useEffect(() => {
        setSelectedFromValue(defaultFromValue);
    }, [defaultFromValue]);

    useEffect(() => {
        setSelectedToValue(defaultToValue);
    }, [defaultToValue]);

    const hasMonthlyPrice = !_.isNil(minMonthlyPrice) && !_.isNil(maxMonthlyPrice);
    const hasFullPrice = !_.isNil(minFullPrice) && !_.isNil(maxFullPrice);

    if (!hasMonthlyPrice && !hasFullPrice) {
        return null;
    }

    const minValue = Math.floor(selectedType === 'FULL' ? minFullPrice : minMonthlyPrice);
    const maxValue = Math.ceil(selectedType === 'FULL' ? maxFullPrice : maxMonthlyPrice);

    const handlePriceTypeChange = (value: PriceFilterType) => {
        const from = Math.floor(value === 'FULL' ? minFullPrice : minMonthlyPrice);
        const to = Math.ceil(value === 'FULL' ? maxFullPrice : maxMonthlyPrice);
        setSelectedType(value);
        setSelectedFromValue(from);
        setSelectedToValue(to);
        onChange(value, from, to);
    };

    const handleRangeChange = ([from, to]: [number, number]) => {
        setSelectedFromValue(from);
        setSelectedToValue(to);
        onChange(selectedType, from, to);
    };

    const formatInputLabel = (value: number) => {
        return value.toFixed(0);
    };

    const parseInputLabel = (label: string) => {
        return /^[0-9]+$/.test(label) ? parseFloat(label) : undefined;
    };

    return (
        <div>
            {hasMonthlyPrice && hasFullPrice && (
                <PriceTypeRadioContainer>
                    <RadioButtonGroup
                        value={selectedType}
                        id="radio-button-story"
                        items={[
                            {
                                id: 'MONTHLY-' + componentId,
                                label: t['search.component.price_filter.monthly_price'],
                                value: 'MONTHLY',
                            },
                            {
                                id: 'FULL-' + componentId,
                                label: t['search.component.price_filter.full_price'],
                                value: 'FULL',
                            },
                        ]}
                        onValueChange={handlePriceTypeChange}
                        data-test={SEARCH_TEST_ID.FACETS_PRICE_FILTER_TYPE_CHANGE}
                    />
                </PriceTypeRadioContainer>
            )}
            {hasMonthlyPrice && !hasFullPrice && (
                <PriceTypeTitleContainer>{t['search.component.price_filter.monthly_price']}</PriceTypeTitleContainer>
            )}
            {hasFullPrice && !hasMonthlyPrice && (
                <PriceTypeTitleContainer>{t['search.component.price_filter.full_price']}</PriceTypeTitleContainer>
            )}

            <RangeSlider
                minValue={minValue}
                maxValue={maxValue}
                fromValue={selectedFromValue}
                toValue={selectedToValue}
                inputAdornment={<span>{t['search.component.price_filter.unit']}</span>}
                dataTest={SEARCH_TEST_ID.FACETS_PRICE_FILTER}
                ariaLabel={
                    selectedType === 'FULL'
                        ? t['search.component.price_filter.full_price']
                        : t['search.component.price_filter.monthly_price']
                }
                onChange={handleRangeChange}
                formatLabel={formatInputLabel}
                parseLabel={parseInputLabel}
            />
        </div>
    );
};
